import { ASTNode, print } from "graphql";

import { AggregationOperation, FieldFilter, GridSort, SummarizeBy } from "../../schemas/report";

import {
	GET_EMPLOYEE_REPORT_DATA_COUNT,
	GET_EMPLOYEE_REPORT_DISTRIBUTION_DATA,
	GET_GOAL_REPORT_DATA_COUNT,
	GET_GOAL_REPORT_DISTRIBUTION_DATA,
	GET_RAW_REPORT_DATA,
	GET_REVIEW_REPORT_DATA_COUNT,
	GET_REVIEW_REPORT_DISTRIBUTION_DATA,
	GET_SURVEY_REPORT_DATA_COUNT,
	GET_SURVEY_REPORT_DISTRIBUTION_DATA,
} from "../../hooks/useQuery";

import { fetchData } from "./utils";

const reportTypeVariableMap = {
	reviewReport: {
		queryName: "reviewReportsVisualization",
		sortType: "ReviewReportsSort",
		summarizeByType: "ReviewReportsSummarizeBy",
		aggregationOperationType: "ReviewReportsAggregationOperation",
	},
	surveyReport: {
		queryName: "surveyReportsVisualization",
		sortType: "SurveyReportsSort",
		summarizeByType: "SurveyReportsSummarizeBy",
		aggregationOperationType: "SurveyReportsAggregationOperation",
	},
	goalReport: {
		queryName: "goalReportsVisualization",
		sortType: "GoalReportsSort",
		summarizeByType: "GoalReportsSummarizeBy",
		aggregationOperationType: "GoalReportsAggregationOperation",
	},
	employeeReport: {
		queryName: "employeeReportsVisualization",
		sortType: "EmployeeReportsSort",
		summarizeByType: "EmployeeReportsSummarizeBy",
		aggregationOperationType: "EmployeeReportsAggregationOperation",
	},
};

export const getRawReviewReportData = (variables, reportType: string = "reviewReport") => {
	const isSurveyReport = reportType === "surveyReport";
	const queryName = reportTypeVariableMap?.[reportType]?.queryName;
	const sortType = reportTypeVariableMap?.[reportType]?.sortType;
	const summarizeByType = reportTypeVariableMap?.[reportType]?.summarizeByType;
	const aggregationOperationType = reportTypeVariableMap?.[reportType]?.aggregationOperationType;

	return fetchData(
		`
            query GetReportData(
                $questionGroupingSessionID: ObjectID!
                $columns: JSON
                $pageNumber: String
                $pageSize: String
                $filters: [[FilterItem]]
                $sorts: [${sortType}]
                $summarizeBy: [${summarizeByType}]
                $aggregationOperations: [${aggregationOperationType}]
            ) {
                ${queryName}(
                    questionGroupingSessionID: $questionGroupingSessionID
                    columns: $columns
                    filters: $filters
                    pageNumber: $pageNumber
                    pageSize: $pageSize
                    sorts: $sorts
                    summarizeBy: $summarizeBy
                    aggregationOperations: $aggregationOperations
                )
            }
        `,
		variables
	).then((response) => {
		// Normalize the response key so charts don't need to know about report types
		if (isSurveyReport && response?.surveyReportsVisualization) {
			return {
				...response,
				reviewReportsVisualization: response.surveyReportsVisualization,
			};
		} else if (reportType === "goalReport" && response?.goalReportsVisualization) {
			return {
				...response,
				reviewReportsVisualization: response.goalReportsVisualization,
			};
		} else if (reportType === "employeeReport" && response?.employeeReportsVisualization) {
			return {
				...response,
				reviewReportsVisualization: response.employeeReportsVisualization,
			};
		}
		return response;
	});
};

export const getGridDataCount = (variables, reportType: string = "reviewReport") => {
	const isSurveyReport = reportType === "surveyReport";
	let queryToUse = GET_REVIEW_REPORT_DATA_COUNT;
	if (reportType === "surveyReport") {
		queryToUse = GET_SURVEY_REPORT_DATA_COUNT;
	} else if (reportType === "goalReport") {
		queryToUse = GET_GOAL_REPORT_DATA_COUNT;
	} else if (reportType === "employeeReport") {
		queryToUse = GET_EMPLOYEE_REPORT_DATA_COUNT;
	}
	return fetchData(print(queryToUse as ASTNode), variables).then((response) => {
		// Normalize the response key so charts don't need to know about report types
		if (isSurveyReport && response?.surveyReportsCount) {
			response = {
				...response,
				reviewReportsCount: response.surveyReportsCount,
			};
		} else if (reportType === "goalReport" && response?.goalReportsCount) {
			response = {
				...response,
				reviewReportsCount: response.goalReportsCount,
			};
		} else if (reportType === "employeeReport" && response?.employeeReportsCount) {
			response = {
				...response,
				reviewReportsCount: response.employeeReportsCount,
			};
		}

		const count = response?.reviewReportsCount?.count;

		// set to large number if timed out
		if (count === -1) {
			return {
				...response,
				reviewReportsCount: { ...response.reviewReportsCount, count: 100000 },
			};
		}

		return response;
	});
};

export const getReviewReportDistributionData = (variables) =>
	fetchData(print(GET_REVIEW_REPORT_DISTRIBUTION_DATA as ASTNode), variables);

export const getGoalReportDistributionData = (variables) =>
	fetchData(print(GET_GOAL_REPORT_DISTRIBUTION_DATA as ASTNode), variables);
export const getSurveyReportDistributionData = (variables) =>
	fetchData(print(GET_SURVEY_REPORT_DISTRIBUTION_DATA as ASTNode), variables);
export const getEmployeeReportDistributionData = (variables) =>
	fetchData(print(GET_EMPLOYEE_REPORT_DISTRIBUTION_DATA as ASTNode), variables);

type ReportsType = "reviewReports" | "employeeReports" | "goalReports" | "surveyReports";

export type NormalizedReportVariables = {
	pageNumber: string;
	pageSize: string;
	columns: { id: string; type: string }[];
	filters: FieldFilter[][];
	sorts: GridSort[];
	summarizeBy: SummarizeBy[];
	aggregationOperations: AggregationOperation[];
	questionGroupingSessionID?: string;
};

export const getReportGridData = (
	variables: NormalizedReportVariables,
	queryName: string,
	reportsType: ReportsType,
	sorts: string,
	summarizeBy: string,
	aggregationOperations: string
) => {
	return fetchData(
		print(GET_RAW_REPORT_DATA(queryName, reportsType, sorts, summarizeBy, aggregationOperations) as ASTNode),
		variables
	).then((data) => data?.[reportsType] ?? []);
};
